import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { setPrinterRunning } from "../../actions/printer";
import { lastSeenText } from "../../utils/heartbeat";
import {
  HEALTH,
  printerProblemsOf,
  lowPapersOf,
  inkLow,
} from "../../utils/printerHealth";
import { rearTrayStat } from "../../utils/rearTray";
import { isListed } from "../../utils/place";

// One paper of the kiosk: what it is, over the sheets it has left. A low count is in
// yellow, unless it is from before the kiosk went quiet.
const paperStat = (label, sheets, low, stale) => {
  let color = "text-gray-700 dark:text-gray-300";
  if (stale) {
    color = "text-gray-400 dark:text-gray-500";
  } else if (low) {
    color = "text-yellow-600 dark:text-yellow-400";
  }
  return (
    <div className="mr-5">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={"text-sm font-semibold tabular-nums " + color}>{sheets}</p>
    </div>
  );
};

// One ink tank, as the backend estimates it: the days it lasts at the kiosk's pace, or
// its marks when the kiosk has not printed lately, or a dash until someone records it
// under Paper & ink
const inkStat = (label, tank, stale) => {
  let text = "—";
  let title = "No ink recorded yet: record it under Edit, Paper & ink";
  if (tank) {
    text =
      typeof tank.daysLeft === "number"
        ? Math.floor(tank.daysLeft) + "d"
        : tank.level + "/" + tank.marks;
    title = tank.level + " of " + tank.marks + " marks, about " + tank.pagesLeft + " pages";
  }
  let color = "text-gray-700 dark:text-gray-300";
  if (stale || !tank) {
    color = "text-gray-400 dark:text-gray-500";
  } else if (inkLow(tank)) {
    color = "text-yellow-600 dark:text-yellow-400";
  }
  return (
    <div className="mr-5" title={title}>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={"text-sm font-semibold tabular-nums " + color}>{text}</p>
    </div>
  );
};

// ink: the kiosk's tanks from GET /api/printer/ink, once loaded
const PrinterItem = ({ printer, health, openPrinter, setPrinterRunning, ink }) => {
  const { label, badge } = HEALTH[health];
  const low = lowPapersOf(printer);
  const tray = rearTrayStat(printer);
  // Shown under an unresolved error too, which takes the badge.
  const problems = printerProblemsOf(printer).join(" ");
  const version =
    printer.telemetry && printer.telemetry.app && printer.telemetry.app.version;
  // The paper count of an offline kiosk is what it had before it went quiet.
  const stale = health === "offline";
  // The "No heartbeat" badge already says it when nothing else is wrong.
  let seen = "";
  if (printer.lastSeenAt) {
    seen = "Last seen " + lastSeenText(printer);
  } else if (health !== "never") {
    seen = "No heartbeat yet";
  }
  const open = () => openPrinter(printer);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={
        "p-4 bg-white rounded-lg shadow-xs cursor-pointer dark:bg-gray-800 hover:shadow-md focus:outline-none focus:shadow-outline-purple" +
        (health === "disabled" ? " opacity-60" : "")
      }
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="font-semibold text-gray-700 truncate dark:text-gray-200">
            {printer.printerName}
          </p>
          <p className="text-xs text-gray-600 truncate dark:text-gray-400">
            {isListed(printer) && (
              <i
                className="mr-1 text-purple-500 fas fa-map-marker-alt"
                title="Shown in the kiosks' Locations window"
              />
            )}
            {printer.location ? printer.location : "No location"}
          </p>
        </div>
        <span
          className={
            "flex-shrink-0 ml-2 px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
            badge
          }
        >
          {label}
        </span>
      </div>

      {seen && (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{seen}</p>
      )}
      {stale && printer.status === "error" && (
        <p className="text-xs text-red-600 dark:text-red-400">
          + unresolved error
        </p>
      )}
      {problems && (
        <p
          className="text-xs text-red-600 truncate dark:text-red-400"
          title={problems}
        >
          {problems}
        </p>
      )}

      {health === "error" ? (
        <div className="flex items-center justify-between mt-3">
          <p className="min-w-0 text-sm text-gray-700 truncate dark:text-gray-300">
            {printer.error ? printer.error : "Unknown error"}
          </p>
          <button
            className="flex-shrink-0 px-3 py-2 ml-2 text-sm font-medium leading-5 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple"
            onClick={(e) => {
              e.stopPropagation();
              setPrinterRunning(printer._id, "Error marked as resolved");
            }}
          >
            Resolved
          </button>
        </div>
      ) : (
        <div className="flex items-end justify-between mt-3">
          <div className="flex items-end">
            {paperStat("A4", printer.paper ? printer.paper : 0, low.a4, stale)}
            {tray && paperStat(tray.label, tray.sheets, low.rearTray, stale)}
            {ink && inkStat("Black", ink.black, stale)}
            {ink && inkStat("Color", ink.color, stale)}
            {stale && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                last known
              </p>
            )}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {version}
          </span>
        </div>
      )}
    </div>
  );
};

PrinterItem.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { setPrinterRunning })(PrinterItem);
